/**
 * Calendar-date helpers for fr-CA / America/Montreal (no locale or TZ switching).
 * Prefer these over Moment. Arithmetic and display use America/Montreal wall time,
 * matching `moment.tz.setDefault('America/Montreal')`.
 */

export const DATE_LOCALE = 'fr-CA';

export const DATE_TIMEZONE = 'America/Montreal';

/** Sentinel used by open-ended services (MomentPipe used year > 4000). */
export const INDETERMINATE_YEAR_THRESHOLD = 4000;

export const INDETERMINATE_LABEL = 'Indéterminée';

export type DateInput = Date | string | number | null | undefined;

/** Moment-compatible unit names (singular and plural). */
export type DateUnit = 'day' | 'days' | 'week' | 'weeks' | 'month' | 'months' | 'year' | 'years';

export type CanonicalDateUnit = 'day' | 'week' | 'month' | 'year';

/** Moment format tokens used in this codebase. */
export type DateFormatToken = 'LL' | 'LLL' | 'LLLL' | 'YYYY-MM-DD' | 'D MMM' | 'dddd' | 'DD' | 'D' | 'M' | 'DD MMMM YYYY';

interface ZonedParts {
	year: number;
	month: number;
	day: number;
	hour: number;
	minute: number;
	second: number;
	weekday: number;
}

const ISO_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

const zonedPartsFormatter = new Intl.DateTimeFormat('en-US', {
	timeZone: DATE_TIMEZONE,
	year: 'numeric',
	month: '2-digit',
	day: '2-digit',
	hour: '2-digit',
	minute: '2-digit',
	second: '2-digit',
	hour12: false
});

const longDateFormatter = new Intl.DateTimeFormat(DATE_LOCALE, {
	timeZone: DATE_TIMEZONE,
	dateStyle: 'long'
});
const longDateTimeFormatter = new Intl.DateTimeFormat(DATE_LOCALE, {
	timeZone: DATE_TIMEZONE,
	dateStyle: 'long',
	timeStyle: 'short'
});
const fullDateTimeFormatter = new Intl.DateTimeFormat(DATE_LOCALE, {
	timeZone: DATE_TIMEZONE,
	dateStyle: 'full',
	timeStyle: 'short'
});
const isoDateFormatter = new Intl.DateTimeFormat('en-CA', {
	timeZone: DATE_TIMEZONE,
	year: 'numeric',
	month: '2-digit',
	day: '2-digit'
});
const dayMonthFormatter = new Intl.DateTimeFormat(DATE_LOCALE, {
	timeZone: DATE_TIMEZONE,
	day: 'numeric',
	month: 'short'
});
const weekdayFormatter = new Intl.DateTimeFormat(DATE_LOCALE, {
	timeZone: DATE_TIMEZONE,
	weekday: 'long'
});
const longNumericDateFormatter = new Intl.DateTimeFormat(DATE_LOCALE, {
	timeZone: DATE_TIMEZONE,
	day: '2-digit',
	month: 'long',
	year: 'numeric'
});

function canonicalUnit(unit: DateUnit): CanonicalDateUnit {
	if (unit === 'day' || unit === 'days') {
		return 'day';
	}
	if (unit === 'week' || unit === 'weeks') {
		return 'week';
	}
	if (unit === 'month' || unit === 'months') {
		return 'month';
	}
	return 'year';
}

function weekdayFromCivil(year: number, month: number, day: number): number {
	return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

function getZonedParts(date: Date): ZonedParts {
	const map: Record<string, string> = {};
	for (const part of zonedPartsFormatter.formatToParts(date)) {
		if (part.type !== 'literal') {
			map[part.type] = part.value;
		}
	}
	const year = Number(map.year);
	const month = Number(map.month);
	const day = Number(map.day);
	return {
		year,
		month,
		day,
		hour: Number(map.hour) % 24,
		minute: Number(map.minute),
		second: Number(map.second),
		weekday: weekdayFromCivil(year, month, day)
	};
}

/** Milliseconds to add to UTC to obtain Montreal wall-clock as if it were UTC. */
function zonedOffsetMs(date: Date): number {
	const parts = getZonedParts(date);
	const wallAsUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
	return wallAsUtc - date.getTime();
}

function fromZonedCivil(year: number, month: number, day: number, hour = 0, minute = 0, second = 0, ms = 0): Date {
	const utcGuess = Date.UTC(year, month - 1, day, hour, minute, second);
	const first = new Date(utcGuess - zonedOffsetMs(new Date(utcGuess)));
	const instant = utcGuess - zonedOffsetMs(first);
	return new Date(instant + ms);
}

function zonedPartsOf(value: DateInput): ZonedParts | null {
	const d = toDate(value);
	return d ? getZonedParts(d) : null;
}

export function now(): Date {
	return new Date();
}

export function toDate(value: DateInput): Date | null {
	if (value == null || value === '') {
		return null;
	}
	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : new Date(value.getTime());
	}
	if (typeof value === 'string') {
		const isoDate = ISO_DATE_ONLY.exec(value);
		if (isoDate) {
			return fromZonedCivil(Number(isoDate[1]), Number(isoDate[2]), Number(isoDate[3]));
		}
	}
	const d = new Date(value);
	return Number.isNaN(d.getTime()) ? null : d;
}

export function isValidDate(value: DateInput): boolean {
	return toDate(value) != null;
}

/** Montreal calendar year. Equivalent to Moment `year()` / `get('year')`. */
export function getYear(value: DateInput): number | null {
	const parts = zonedPartsOf(value);
	return parts ? parts.year : null;
}

/** Montreal month, 0-indexed like Moment `month()` / `get('month')`. */
export function getMonth(value: DateInput): number | null {
	const parts = zonedPartsOf(value);
	return parts ? parts.month - 1 : null;
}

/** Montreal day of month (1–31). Not a `Date` object — use `toDate()` for that. */
export function getDayOfMonth(value: DateInput): number | null {
	const parts = zonedPartsOf(value);
	return parts ? parts.day : null;
}

/** Weekday 0=Sunday … 6=Saturday. Equivalent to Moment `day()`. */
export function getWeekday(value: DateInput): number | null {
	const parts = zonedPartsOf(value);
	return parts ? parts.weekday : null;
}

export function isWeekend(value: DateInput): boolean {
	const weekday = getWeekday(value);
	return weekday === 0 || weekday === 6;
}

export function isIndeterminate(value: DateInput): boolean {
	const year = getYear(value);
	return year != null && year > INDETERMINATE_YEAR_THRESHOLD;
}

/** Equivalent to Moment `format('LL')`. */
export function formatDate(value: DateInput): string {
	if (isIndeterminate(value)) {
		return INDETERMINATE_LABEL;
	}
	const d = toDate(value);
	return d ? longDateFormatter.format(d) : '';
}

/** Equivalent to Moment `format('LLL')`. */
export function formatDateTime(value: DateInput): string {
	if (isIndeterminate(value)) {
		return INDETERMINATE_LABEL;
	}
	const d = toDate(value);
	return d ? longDateTimeFormatter.format(d) : '';
}

/** Equivalent to Moment `format('LLLL')` (full weekday + date + time). */
export function formatDateTimeFull(value: DateInput): string {
	if (isIndeterminate(value)) {
		return INDETERMINATE_LABEL;
	}
	const d = toDate(value);
	return d ? fullDateTimeFormatter.format(d) : '';
}

/** Equivalent to Moment `format('YYYY-MM-DD')` in America/Montreal. */
export function formatIsoDate(value: DateInput): string {
	const d = toDate(value);
	return d ? isoDateFormatter.format(d) : '';
}

/** Equivalent to Moment `format('D MMM')`. */
export function formatDayMonth(value: DateInput): string {
	const d = toDate(value);
	return d ? dayMonthFormatter.format(d) : '';
}

/** Equivalent to Moment `format('dddd')`. */
export function formatWeekday(value: DateInput): string {
	const d = toDate(value);
	return d ? weekdayFormatter.format(d) : '';
}

/** Equivalent to Moment `format('DD MMMM YYYY')`. */
export function formatLongNumericDate(value: DateInput): string {
	const d = toDate(value);
	return d ? longNumericDateFormatter.format(d) : '';
}

/** Format with a Moment token (`LL`, `D MMM`, …). Prefer the named formatters when the token is fixed. */
export function formatByToken(value: DateInput, token: DateFormatToken): string {
	if (token === 'LL') {
		return formatDate(value);
	}
	if (token === 'LLL') {
		return formatDateTime(value);
	}
	if (token === 'LLLL') {
		return formatDateTimeFull(value);
	}
	if (token === 'YYYY-MM-DD') {
		return formatIsoDate(value);
	}
	if (token === 'D MMM') {
		return formatDayMonth(value);
	}
	if (token === 'dddd') {
		return formatWeekday(value);
	}
	if (token === 'DD MMMM YYYY') {
		return formatLongNumericDate(value);
	}
	const day = getDayOfMonth(value);
	if (day == null) {
		return '';
	}
	if (token === 'DD') {
		return String(day).padStart(2, '0');
	}
	if (token === 'D') {
		return String(day);
	}
	const month = getMonth(value);
	return month == null ? '' : String(month + 1);
}

export function fromUnix(seconds: number): Date {
	return new Date(seconds * 1000);
}

/** Equivalent to Moment `unix()` (seconds). */
export function toUnix(value: DateInput): number | null {
	const d = toDate(value);
	return d ? Math.floor(d.getTime() / 1000) : null;
}

export function today(): Date {
	return startOfDay(now())!;
}

export function startOfDay(value: DateInput): Date | null {
	const parts = zonedPartsOf(value);
	if (!parts) {
		return null;
	}
	return fromZonedCivil(parts.year, parts.month, parts.day);
}

export function endOfDay(value: DateInput): Date | null {
	const parts = zonedPartsOf(value);
	if (!parts) {
		return null;
	}
	return fromZonedCivil(parts.year, parts.month, parts.day, 23, 59, 59, 999);
}

export function startOfMonth(value: DateInput): Date | null {
	const parts = zonedPartsOf(value);
	if (!parts) {
		return null;
	}
	return fromZonedCivil(parts.year, parts.month, 1);
}

export function endOfMonth(value: DateInput): Date | null {
	const parts = zonedPartsOf(value);
	if (!parts) {
		return null;
	}
	const lastDay = daysInMonth(value);
	return fromZonedCivil(parts.year, parts.month, lastDay, 23, 59, 59, 999);
}

export function startOfYear(value: DateInput): Date | null {
	const parts = zonedPartsOf(value);
	if (!parts) {
		return null;
	}
	return fromZonedCivil(parts.year, 1, 1);
}

export function endOfYear(value: DateInput): Date | null {
	const parts = zonedPartsOf(value);
	if (!parts) {
		return null;
	}
	return fromZonedCivil(parts.year, 12, 31, 23, 59, 59, 999);
}

/** Sunday 00:00 Montreal, matching Moment fr_CA `startOf('week')` (dow = 0). */
export function startOfWeek(value: DateInput): Date | null {
	const parts = zonedPartsOf(value);
	if (!parts) {
		return null;
	}
	return fromZonedCivil(parts.year, parts.month, parts.day - parts.weekday);
}

export function endOfWeek(value: DateInput): Date | null {
	const start = startOfWeek(value);
	if (!start) {
		return null;
	}
	const parts = getZonedParts(start);
	return fromZonedCivil(parts.year, parts.month, parts.day + 6, 23, 59, 59, 999);
}

export function daysInMonth(value: DateInput): number {
	const parts = zonedPartsOf(value);
	if (!parts) {
		return 0;
	}
	return new Date(Date.UTC(parts.year, parts.month, 0)).getUTCDate();
}

export function addDays(value: DateInput, amount: number): Date | null {
	const d = toDate(value);
	if (!d) {
		return null;
	}
	const parts = getZonedParts(d);
	const ms = d.getTime() % 1000;
	return fromZonedCivil(parts.year, parts.month, parts.day + amount, parts.hour, parts.minute, parts.second, ms);
}

export function addWeeks(value: DateInput, amount: number): Date | null {
	return addDays(value, amount * 7);
}

/** Clamps to last day of month when the source day does not exist (e.g. Jan 31 + 1 month). */
export function addMonths(value: DateInput, amount: number): Date | null {
	const d = toDate(value);
	if (!d) {
		return null;
	}
	const parts = getZonedParts(d);
	const monthIndex = parts.month - 1 + amount;
	const year = parts.year + Math.floor(monthIndex / 12);
	const month = ((monthIndex % 12) + 12) % 12;
	const dim = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
	const day = Math.min(parts.day, dim);
	const ms = d.getTime() % 1000;
	return fromZonedCivil(year, month + 1, day, parts.hour, parts.minute, parts.second, ms);
}

export function addYears(value: DateInput, amount: number): Date | null {
	return addMonths(value, amount * 12);
}

export function subtractDays(value: DateInput, amount: number): Date | null {
	return addDays(value, -amount);
}

export function subtractWeeks(value: DateInput, amount: number): Date | null {
	return addWeeks(value, -amount);
}

export function subtractMonths(value: DateInput, amount: number): Date | null {
	return addMonths(value, -amount);
}

export function subtractYears(value: DateInput, amount: number): Date | null {
	return addYears(value, -amount);
}

/** Milliseconds of `date` minus `fromDate` (negative if `date` is earlier). */
export function millisecondsSince(date: DateInput, fromDate: DateInput): number {
	const left = toDate(date);
	const right = toDate(fromDate);
	if (!left || !right) {
		return NaN;
	}
	return left.getTime() - right.getTime();
}

/** Days of `date` minus `fromDate` (Moment `date.diff(fromDate, 'days')`). */
export function daysSince(date: DateInput, fromDate: DateInput, floating = false): number {
	const ms = millisecondsSince(date, fromDate);
	if (Number.isNaN(ms)) {
		return NaN;
	}
	const days = ms / 86_400_000;
	return floating ? days : Math.trunc(days);
}

export function weeksSince(date: DateInput, fromDate: DateInput, floating = false): number {
	const days = daysSince(date, fromDate, true);
	if (Number.isNaN(days)) {
		return NaN;
	}
	const weeks = days / 7;
	return floating ? weeks : Math.trunc(weeks);
}

/**
 * Calendar months of `date` minus `fromDate` in America/Montreal.
 * When `floating` is true, includes a day fraction (billing ceilings).
 */
export function monthsSince(date: DateInput, fromDate: DateInput, floating = false): number {
	const left = zonedPartsOf(date);
	const right = zonedPartsOf(fromDate);
	if (!left || !right) {
		return NaN;
	}
	const months = (left.year - right.year) * 12 + (left.month - right.month);
	if (!floating) {
		return months;
	}
	const dim = daysInMonth(fromDate);
	return months + (left.day - right.day) / dim;
}

export function yearsSince(date: DateInput, fromDate: DateInput, floating = false): number {
	const left = zonedPartsOf(date);
	const right = zonedPartsOf(fromDate);
	if (!left || !right) {
		return NaN;
	}
	if (!floating) {
		return left.year - right.year;
	}
	return monthsSince(date, fromDate, true) / 12;
}

function startOfUnit(value: Date, unit: CanonicalDateUnit): Date {
	if (unit === 'day') {
		return startOfDay(value)!;
	}
	if (unit === 'week') {
		return startOfWeek(value)!;
	}
	if (unit === 'month') {
		return startOfMonth(value)!;
	}
	return startOfYear(value)!;
}

/** True when `date` is earlier than `than` (optionally compared at calendar `unit`). */
export function isDateBefore(date: DateInput, than: DateInput, unit?: DateUnit): boolean {
	const left = toDate(date);
	const right = toDate(than);
	if (!left || !right) {
		return false;
	}
	if (!unit) {
		return left.getTime() < right.getTime();
	}
	return startOfUnit(left, canonicalUnit(unit)).getTime() < startOfUnit(right, canonicalUnit(unit)).getTime();
}

/** True when `date` is later than `than` (optionally compared at calendar `unit`). */
export function isDateAfter(date: DateInput, than: DateInput, unit?: DateUnit): boolean {
	const left = toDate(date);
	const right = toDate(than);
	if (!left || !right) {
		return false;
	}
	if (!unit) {
		return left.getTime() > right.getTime();
	}
	return startOfUnit(left, canonicalUnit(unit)).getTime() > startOfUnit(right, canonicalUnit(unit)).getTime();
}

/** True when `date` and `other` fall in the same Montreal calendar `unit` (default: day). */
export function isSameAs(date: DateInput, other: DateInput, unit: DateUnit = 'day'): boolean {
	const left = toDate(date);
	const right = toDate(other);
	if (!left || !right) {
		return false;
	}
	return startOfUnit(left, canonicalUnit(unit)).getTime() === startOfUnit(right, canonicalUnit(unit)).getTime();
}

export function isOnOrBefore(date: DateInput, than: DateInput, unit?: DateUnit): boolean {
	return isSameAs(date, than, unit ?? 'day') || isDateBefore(date, than, unit);
}

export function isOnOrAfter(date: DateInput, than: DateInput, unit?: DateUnit): boolean {
	return isSameAs(date, than, unit ?? 'day') || isDateAfter(date, than, unit);
}

export function toISOString(value: DateInput): string | null {
	const d = toDate(value);
	return d ? d.toISOString() : null;
}

/** Relative time in fr-CA (Moment `fromNow()`). */
export function fromNow(value: DateInput): string {
	const d = toDate(value);
	if (!d) {
		return '';
	}
	const diffMs = d.getTime() - Date.now();
	const rtf = new Intl.RelativeTimeFormat(DATE_LOCALE, { numeric: 'auto' });
	const absSec = Math.round(Math.abs(diffMs) / 1000);
	const sign = diffMs < 0 ? -1 : 1;

	if (absSec < 60) {
		return rtf.format(sign * absSec, 'second');
	}
	const absMin = Math.round(absSec / 60);
	if (absMin < 60) {
		return rtf.format(sign * absMin, 'minute');
	}
	const absHour = Math.round(absMin / 60);
	if (absHour < 24) {
		return rtf.format(sign * absHour, 'hour');
	}
	const absDay = Math.round(absHour / 24);
	if (absDay < 30) {
		return rtf.format(sign * absDay, 'day');
	}
	const absMonth = Math.round(absDay / 30);
	if (absMonth < 12) {
		return rtf.format(sign * absMonth, 'month');
	}
	return rtf.format(sign * Math.round(absMonth / 12), 'year');
}
